import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { ragApi } from '@/api/rag';
import { unwrapError } from '@/api/client';
import type { RagDocumentSummary } from '@/api/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/lib/utils';

const scopeVariant = {
  ops: 'destructive',
  client: 'secondary',
  all: 'success',
} as const;

export function DocumentsTable(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['rag', 'docs'],
    queryFn: () => ragApi.listDocuments(),
  });

  const deleteMutation = useMutation({
    mutationFn: (documentId: string) => ragApi.deleteDocument(documentId),
    onSuccess: (_, documentId) => {
      toast.success(t('rag.documentDeleted', { id: documentId.slice(0, 8) }));
      void queryClient.invalidateQueries({ queryKey: ['rag', 'docs'] });
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  const items: RagDocumentSummary[] = query.data ?? [];

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base">
          {t('rag.documentsTitle')}{' '}
          {query.data ? (
            <span className="ml-1 text-sm font-normal text-muted-foreground">
              {t('rag.documentsCount', { count: items.length })}
            </span>
          ) : null}
        </CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void query.refetch()}
          disabled={query.isFetching}
          className="gap-2"
        >
          <RefreshCw className={query.isFetching ? 'h-3 w-3 animate-spin' : 'h-3 w-3'} />
          {t('common.refresh')}
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {query.isLoading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('common.loading')}
          </div>
        ) : query.error ? (
          <p className="p-6 text-sm text-destructive">{unwrapError(query.error)}</p>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-12 text-center">
            <FileText className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{t('rag.noDocs')}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('rag.tableHeader.filename')}</TableHead>
                <TableHead>{t('rag.tableHeader.scope')}</TableHead>
                <TableHead>{t('rag.tableHeader.tags')}</TableHead>
                <TableHead className="text-right">{t('rag.tableHeader.size')}</TableHead>
                <TableHead>{t('rag.tableHeader.uploadedBy')}</TableHead>
                <TableHead>{t('rag.tableHeader.when')}</TableHead>
                <TableHead className="w-[60px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((d) => (
                <TableRow key={d.documentId}>
                  <TableCell>
                    <div className="font-medium">{d.metadata.title ?? d.filename}</div>
                    {d.metadata.title ? (
                      <div className="text-[10px] text-muted-foreground">{d.filename}</div>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Badge variant={scopeVariant[d.metadata.scope] ?? 'outline'}>
                      {d.metadata.scope}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(d.metadata.tags ?? []).slice(0, 4).map((tag) => (
                        <Badge key={tag} variant="outline" className="text-[10px]">
                          {tag}
                        </Badge>
                      ))}
                      {(d.metadata.tags?.length ?? 0) > 4 ? (
                        <span className="text-[10px] text-muted-foreground">
                          +{(d.metadata.tags?.length ?? 0) - 4}
                        </span>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="text-right text-xs">{formatBytes(d.size)}</TableCell>
                  <TableCell className="text-xs">{d.uploadedBy ?? '—'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDate(d.uploadedAt ?? d.modifiedAt)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (window.confirm(t('rag.confirmDelete', { name: d.filename }))) {
                          deleteMutation.mutate(d.documentId);
                        }
                      }}
                      disabled={deleteMutation.isPending}
                      aria-label={t('rag.deleteAriaLabel', { name: d.filename })}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
}
